import { v4 as uuidv4 } from 'uuid'
import { type TrackSession } from '@pitbrain/shared'
import { useTrackSessionContext } from '../trackSession/TrackSessionContext'
import { type UploadFormat } from './UploadFormat'
import { useUploadFormats } from './useUploadFormats'
import { useStoredSessionLoader } from './useStoredSessionLoader'
import { forgetRawTelemetry, rememberRawTelemetry } from './rawTelemetrySource'
import { messageOf } from '@pitbrain/shared'

export interface UseFileParserProps {
  setProgressPercent: React.Dispatch<React.SetStateAction<number>>
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  setError: React.Dispatch<React.SetStateAction<string | undefined | null>>
}

export const useFileParser = ({
  setProgressPercent,
  setIsLoading,
  setError,
}: UseFileParserProps) => {
  const { trackSessions, videoSessions } = useTrackSessionContext()
  const formats = useUploadFormats({ setProgressPercent, setError })
  const { loadStoredSession } = useStoredSessionLoader()

  const formatOf = (fileName: string): UploadFormat | undefined =>
    formats.find((format) =>
      format.extensions.some((extension) =>
        fileName.toUpperCase().endsWith(extension.toUpperCase())
      )
    )

  const hasVideo = (session: TrackSession): boolean =>
    videoSessions.some(
      (v) =>
        v.id === session.id || session.laps.some((l) => l?.sessionId === v.id)
    )

  const parseFile = async (file: File) => {
    const format = formatOf(file.name)
    const sessionsOnPage = trackSessions.filter(
      (ts) => ts.fileName === file.name
    )
    if (sessionsOnPage.length > 0) {
      const [onlySession] = sessionsOnPage
      const addAsVideo = format?.addAsVideo
      if (sessionsOnPage.length === 1 && addAsVideo && !hasVideo(onlySession)) {
        setError(null)
        addAsVideo(onlySession.id, file)
      } else {
        setError('This file is already loaded.')
      }
      return
    }

    const sessionId = uuidv4()

    try {
      setIsLoading(true)
      setError(null)
      if (format === undefined) throw new Error('Unsupported file type')

      const storedSession = await loadStoredSession(file.name)
      if (storedSession) {
        format.addAsVideo?.(storedSession.id, file)
        return
      }

      if (format.keepsFileAsSource) {
        rememberRawTelemetry(sessionId, {
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          content: file,
        })
      }

      await format.processFile(file, sessionId)
    } catch (error: unknown) {
      forgetRawTelemetry(sessionId)
      const errorMessage = messageOf(error)
      console.log('An error occurred while processing the file.', errorMessage)
      setError(`An error occurred while processing the file. ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }

  return {
    parseFile,
    acceptedExtensions: formats.flatMap((format) => format.extensions),
  }
}
